// Recent Drop recipients — a small client-side list (localStorage) that feeds
// the "Recent" shortcuts on the Send details screen. No backend: it's purely a
// per-browser convenience. Each entry is a resolved recipient so a tap can pay
// without re-resolving. Deduped by address, most-recent first, capped.

export interface RecentRecipient {
  address: string;
  display: string; // wallet code (or, from v0.50.2, a saved nickname), else short 0x…
}

const KEY = "m.recent";
const MAX = 6;

export function readRecent(): RecentRecipient[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr
      .filter(
        (r): r is RecentRecipient =>
          !!r &&
          typeof (r as RecentRecipient).address === "string" &&
          typeof (r as RecentRecipient).display === "string",
      )
      .slice(0, MAX);
  } catch {
    return [];
  }
}

export function addRecent(r: RecentRecipient): void {
  try {
    const addr = r.address.toLowerCase();
    const next = [
      { address: r.address, display: r.display },
      ...readRecent().filter((x) => x.address.toLowerCase() !== addr),
    ].slice(0, MAX);
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore — recents are best-effort */
  }
}

/**
 * Refresh a stored recipient's display in place (no reordering) — used to heal a
 * frozen label after that wallet re-codes (v0.50). Only writes if it changed.
 */
export function updateRecentDisplay(address: string, display: string): void {
  try {
    const addr = address.toLowerCase();
    let changed = false;
    const next = readRecent().map((x) => {
      if (x.address.toLowerCase() === addr && x.display !== display) {
        changed = true;
        return { address: x.address, display };
      }
      return x;
    });
    if (changed) localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore — recents are best-effort */
  }
}
